/**
 * Data Normalization Utility
 * Converts n8n JSON response to backend-compatible format
 * Handles both camelCase and Pascal_Case formats from n8n
 * Maps to exact database column names (Pascal_Case)
 */

/**
 * Normalize n8n JSON response for saving to database
 * @param {Object} n8nJsonData - Raw JSON from n8n
 * @returns {Object} Normalized data with exact database column names
 */
export const normalizeHistoryData = (n8nJsonData) => {
  if (!n8nJsonData) return null;

  const data = n8nJsonData;

  // Helper to get value from multiple possible keys
  const getValue = (obj, keys) => {
    if (!Array.isArray(keys)) keys = [keys];
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
        return obj[key];
      }
    }
    return null;
  };

  // n8n may return a single object per section instead of an array; coerce to array for mappers
  const asArray = (v) =>
    Array.isArray(v) ? v : v != null && typeof v === 'object' ? [v] : [];

  // If row has no Sr_No/Serial_No, set 1, 2, 3... (for display and save)
  const SR_NO_KEYS = ['Sr_No', 'SrNo', 'sr_no', 'srno', 'serialNo', 'Serial_No', 'Serial_no', 'SerialNo'];
  const ensureSerialNumbers = (list, primaryKey = 'Sr_No', fallbackKey = 'Serial_No') => {
    if (!Array.isArray(list)) return list;
    return list.map((row, index) => {
      if (!row || typeof row !== 'object') return row;
      const hasVal = SR_NO_KEYS.some((k) => {
        const v = row[k];
        return v != null && String(v).trim() !== '';
      });
      if (!hasVal) {
        const num = index + 1;
        return { ...row, [primaryKey]: num, [fallbackKey]: num };
      }
      return row;
    });
  };

  // Map Challan data - exact column names from history_of_challans table
  const mapChallan = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((c) => ({
      Serial_No: getValue(c, ['Serial_No', 'Serial_no', 'SerialNo', 'serial_no', 'srno', 'serialNo']),
      Challan_No: getValue(c, ['Challan_No', 'ChallanNo', 'challan_no', 'challanNo']),
      Offense_Details: getValue(c, ['Offense_Details_Name_Act', 'Offense_Details', 'OffenseDetails', 'offense_details', 'offenseDetails']),
      Challan_Place: getValue(c, ['Challan_Place', 'ChallanPlace', 'challan_place', 'challanPlace']),
      Challan_Date_Time: getValue(c, ['Challan_Date_Time', 'ChallanDateTime', 'challan_date_time', 'challanDateTime']),
      State: getValue(c, ['State', 'state']),
      RTO: getValue(c, ['RTO', 'rto']),
      Accused_Name: getValue(c, ['Accused_Name', 'Accussed_Name', 'AccusedName', 'accused_name', 'accusedName', 'accussed_name']),
      Amount: getValue(c, ['Amount', 'amount']),
      Challan_Status: getValue(c, ['Challan_Status', 'ChallanStatus', 'challan_status', 'challanStatus']),
      Payment_Source: getValue(c, ['Payment_Source', 'Payment_source', 'PaymentSource', 'payment_source', 'paymentSource']) || '',
      Payment_Date: getValue(c, ['Payment_Date', 'PaymentDate', 'payment_date', 'paymentDate']) || '',
      Receipt_Number: getValue(c, ['Receipt_Number', 'ReceiptNumber', 'receipt_number', 'receiptNumber']) || '',
      sent_to_reg_court: getValue(c, ['sent_to_reg_court', 'sentToRegCourt']) || null,
      remark: getValue(c, ['remark', 'Remark']) || null,
      dl_no: getValue(c, ['dl_no', 'dlNo']) || null,
      driver_name: getValue(c, ['driver_name', 'driverName']) || null,
      owner_name: getValue(c, ['owner_name', 'ownerName']) || null,
      department: getValue(c, ['department', 'Department']) || null,
      document_impounded: getValue(c, ['document_impounded', 'documentImpounded']) || null,
      amount_of_fine_imposed: getValue(c, ['amount_of_fine_imposed', 'amountOfFineImposed']) || null,
      court_address: getValue(c, ['court_address', 'courtAddress']) || null,
      court_name: getValue(c, ['court_name', 'courtName']) || null,
      date_of_proceeding: getValue(c, ['date_of_proceeding', 'dateOfProceeding']) || null,
      sent_to_court_on: getValue(c, ['sent_to_court_on', 'sentToCourtOn']) || null,
      sent_to_virtual_court: getValue(c, ['sent_to_virtual_court', 'sentToVirtualCourt']) || null,
      rto_distric_name: getValue(c, ['rto_distric_name', 'rtoDistricName']) || null,
    }));
  };

  // Map Hypothecation data - exact column names from history_of_hypothecations table
  const mapHypothecation = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((h) => ({
      Application_No: getValue(h, ['Application_No', 'ApplicationNo', 'application_no', 'applicationNo']),
      Hypothecation_Type: getValue(h, ['Hypothecation_Type', 'HypothecationType', 'hypothecation_type', 'hypothecationType']),
      Financer_Name: getValue(h, ['Financer_Name', 'FinancerName', 'financer_name', 'financerName']),
      Financer_Address: getValue(h, ['Financer_Address', 'FinancerAddress', 'financer_address', 'financerAddress']),
      From_Date: getValue(h, ['From_Date', 'FromDate', 'from_date', 'fromDate']),
      To_Date: getValue(h, ['To_Date', 'ToDate', 'to_date', 'toDate']),
      Termination_Date: getValue(h, ['Termination_Date', 'TerminationDate', 'termination_date', 'terminationDate']),
      Office: getValue(h, ['Office', 'office']),
    }));
  };

  // Map Insurance data - exact column names from history_of_insurances table (n8n may send Serial_No)
  const mapInsurance = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((ins) => ({
      Sr_No: getValue(ins, ['Sr_No', 'SrNo', 'sr_no', 'srno', 'srNo', 'Serial_No', 'Serial_no', 'serialNo']),
      Insurance_Company: getValue(ins, ['Insurance_Company', 'InsuranceCompany', 'insurance_company', 'insuranceCompany']),
      Insurance_Type: getValue(ins, ['Insurance_Type', 'InsuranceType', 'insurance_type', 'insuranceType']),
      Insurance_From: getValue(ins, ['Insurance_From', 'InsuranceFrom', 'insurance_from', 'insuranceFrom']),
      Insurance_Upto: getValue(ins, ['Insurance_Upto', 'InsuranceUpto', 'insurance_upto', 'insuranceUpto']),
      Cover_Note_No: getValue(ins, ['Cover_Note_No', 'CoverNoteNo', 'cover_note_no', 'coverNoteNo']),
      Office: getValue(ins, ['Office', 'office']),
    }));
  };

  // Map Paid Fees data - Fee_Collected_As is collection mode (same column)
  const mapPaidFees = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((f) => ({
      Receipt_No: getValue(f, ['Receipt_No', 'ReceiptNo', 'receipt_no', 'receiptNo']),
      Receipt_Date: getValue(f, ['Receipt_Date', 'ReceiptDate', 'receipt_date', 'receiptDate']),
      Fee_Amount: getValue(f, ['Fee_Amount', 'FeeAmount', 'fee_amount', 'feeAmount']),
      Fine: getValue(f, ['Fine', 'fine']),
      Fee_Particular: getValue(f, ['Fee_Particular', 'FeeParticular', 'fee_particular', 'feeParticular']),
      Office: getValue(f, ['Office', 'office']),
      GRN_No: getValue(f, ['GRN_No', 'GRNNo', 'grn_no', 'grnNo']),
      Fee_Collected_As: getValue(f, ['Fee_Collected_As', 'Fee_Collected_as', 'FeeCollectedAs', 'fee_collected_as', 'feeCollectedAs', 'collection_mode', 'Collection_Mode', 'collectionMode', 'CollectionMode']),
    }));
  };

  // Map Road Tax data - exact column names from history_of_road_taxes table.
  // n8n may send receipt number as "Registration No" and collection as "Payment Source".
  const mapRoadTax = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((t) => {
      // n8n often puts actual receipt number in "Registration No"; "Receipt No" may be "0"
      const fromRegNo = getValue(t, ['Registration_No', 'Registration No']);
      const fromReceiptNo = getValue(t, ['Receipt_No', 'ReceiptNo', 'receipt_no', 'receiptNo']);
      const receiptNoValid = (v) => v != null && String(v).trim() !== '' && String(v) !== '0';
      const collectionMode = getValue(t, ['collection_mode', 'Collection_Mode', 'Payment_Source', 'Payment Source', 'collectionMode', 'CollectionMode']);
      return {
        Receipt_No: (receiptNoValid(fromRegNo) ? fromRegNo : null) ?? (receiptNoValid(fromReceiptNo) ? fromReceiptNo : null) ?? null,
        Tax_From: getValue(t, ['Tax_From', 'TaxFrom', 'tax_from', 'taxFrom']),
        Tax_Upto: getValue(t, ['Tax_Upto', 'TaxUpto', 'tax_upto', 'taxUpto']),
        Tax_Type: getValue(t, ['Tax_Type', 'TaxType', 'tax_type', 'taxType']),
        Challan_Date: getValue(t, ['Challan_Date', 'ChallanDate', 'challan_date', 'challanDate']),
        Total_Amount: getValue(t, ['Total_Amount', 'TotalAmount', 'total_amount', 'totalAmount']),
        Office: getValue(t, ['Office', 'office']),
        Tax: getValue(t, ['Tax', 'tax']),
        Penalty: getValue(t, ['Penalty', 'penalty']),
        GRN_No: getValue(t, ['GRN_No', 'GRNNo', 'grn_no', 'grnNo']),
        Breakup: getValue(t, ['Breakup', 'breakup']),
        Tax_Mode: getValue(t, ['Tax_Mode', 'TaxMode', 'tax_mode', 'taxMode']),
        collection_mode: collectionMode,
      };
    });
  };

  // Map Ownership data - exact column names from history_of_transfer_ownerships table
  const mapOwnership = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((o) => ({
      Application_No: getValue(o, ['Application_No', 'ApplicationNo', 'application_no', 'applicationNo']),
      Ownership_Serial: getValue(o, ['Ownership_Serial', 'OwnershipSerial', 'ownership_serial', 'ownershipSerial']),
      Owner_From: getValue(o, ['Owner_From', 'OwnerFrom', 'owner_from', 'ownerFrom']),
      Owner_Upto: getValue(o, ['Owner_Upto', 'OwnerUpto', 'owner_upto', 'ownerUpto']),
      Owner_Name: getValue(o, ['Owner_Name', 'OwnerName', 'owner_name', 'ownerName']),
      Father_Husband_Name: getValue(o, ['Father_Husband_Name', 'FatherHusbandName', 'father_husband_name', 'fatherHusbandName']),
      Present_Address: getValue(o, ['Present_Address', 'PresentAddress', 'present_address', 'presentAddress']),
      Permanent_Address: getValue(o, ['Permanent_Address', 'PermanentAddress', 'permanent_address', 'permanentAddress']),
      Owner_Type: getValue(o, ['Owner_Type', 'OwnerType', 'owner_type', 'ownerType']),
      Sale_Auction_Date: getValue(o, ['Sale_Auction_Date', 'SaleAuctionDate', 'sale_auction_date', 'saleAuctionDate']),
      Sale_Amount: getValue(o, ['Sale_Amount', 'SaleAmount', 'sale_amount', 'saleAmount']),
      Reason: getValue(o, ['Reason', 'reason']),
      Office: getValue(o, ['Office', 'office']),
      Deemed_Transfer_Time_Days: getValue(o, ['Deemed_Transfer_Time_Days', 'DeemedTransferTimeDays', 'deemed_transfer_time_days', 'deemedTransferTimeDays']),
    }));
  };

  // Map Blacklist data - exact column names from history_of_blacklists table (n8n may send Serial_No)
  const mapBlacklist = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((b) => ({
      Sr_No: getValue(b, ['Sr_No', 'SrNo', 'sr_no', 'srno', 'srNo', 'Serial_No', 'Serial_no', 'serialNo']),
      Complain_File_Number: getValue(b, ['Complain_File_Number', 'ComplainFileNumber', 'complain_file_number', 'complainFileNumber']),
      Complain_Date: getValue(b, ['Complain_Date', 'ComplainDate', 'complain_date', 'complainDate']),
      Complain_Entered_By: getValue(b, ['Complain_Entered_By', 'ComplainEnteredBy', 'complain_entered_by', 'complainEnteredBy']),
      Action_Taken: getValue(b, ['Action_Taken', 'ActionTaken', 'action_taken', 'actionTaken']),
      Action_Entered_By: getValue(b, ['Action_Entered_By', 'ActionEnteredBy', 'action_entered_by', 'actionEnteredBy']),
      Action_Date: getValue(b, ['Action_Date', 'ActionDate', 'action_date', 'actionDate']),
      Office: getValue(b, ['Office', 'office']),
      Compounding_Amount: getValue(b, ['Compounding_Amount', 'CompoundingAmount', 'compounding_amount', 'compoundingAmount']),
    }));
  };

  // Map Permit data - exact column names from history_of_permits table (n8n may send Serial_No)
  const mapPermit = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((p) => ({
      Sr_No: getValue(p, ['Sr_No', 'SrNo', 'sr_no', 'srno', 'srNo', 'Serial_No', 'Serial_no', 'serialNo']),
      Application_No: getValue(p, ['Application_No', 'ApplicationNo', 'application_no', 'applicationNo']),
      Permit_No: getValue(p, ['Permit_No', 'PermitNo', 'permit_no', 'permitNo']),
      Issue_Date: getValue(p, ['Issue_Date', 'IssueDate', 'issue_date', 'issueDate']),
      Valid_From: getValue(p, ['Valid_From', 'ValidFrom', 'valid_from', 'validFrom']),
      Valid_Upto: getValue(p, ['Valid_Upto', 'ValidUpto', 'valid_upto', 'validUpto']),
      Permit_Type: getValue(p, ['Permit_Type', 'PermitType', 'permit_type', 'permitType']),
      Permit_Category: getValue(p, ['Permit_Category', 'PermitCategory', 'permit_category', 'permitCategory']),
      Office: getValue(p, ['Office', 'office']),
    }));
  };

  // Map Tax Clear data - exact column names from history_of_tax_clears table (n8n may send Serial_No)
  const mapTaxClear = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((t) => ({
      Sr_No: getValue(t, ['Sr_No', 'SrNo', 'sr_no', 'srno', 'srNo', 'Serial_No', 'Serial_no', 'serialNo']),
      Tax_Description: getValue(t, ['Tax_Description', 'TaxDescription', 'tax_description', 'taxDescription']),
      Tax_Clear_To: getValue(t, ['Tax_Clear_To', 'TaxClearTo', 'tax_clear_to', 'taxClearTo']),
      TCR_No: getValue(t, ['TCR_No', 'TCRNo', 'tcr_no', 'tcrNo']),
      Operation_Date: getValue(t, ['Operation_Date', 'OperationDate', 'operation_date', 'operationDate']),
      Remarks: getValue(t, ['Remarks', 'remarks']),
      Office: getValue(t, ['Office', 'office']),
    }));
  };

  // Map Fitness data - exact column names from history_of_fitness table (all AI-converted fields)
  const mapFitness = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((f) => ({
      Appl_No: getValue(f, ['Appl_No', 'ApplNo', 'appl_no', 'applNo', 'Application_No', 'application_no']),
      Fitness_Check_Date: getValue(f, ['Fitness_Check_Date', 'Fitness_Check_date', 'FitnessCheckDate', 'fitness_check_date', 'fitnessCheckDate']),
      Result: getValue(f, ['Result', 'result']),
      Fitness_UPTO: getValue(f, ['Fitness_UPTO', 'Fitness_Upto', 'FitnessUpto', 'fitness_upto', 'fitnessUpto']),
      NID: getValue(f, ['NID', 'nid']),
      Operation_Date: getValue(f, ['Operation_Date', 'OperationDate', 'operation_date', 'operationDate']),
      Fitness_Officer_Name1: getValue(f, ['Fitness_Officer_Name1', 'FitnessOfficerName1', 'fitness_officer_name1', 'fitnessOfficerName1']),
      Fitness_Officer_Name2: getValue(f, ['Fitness_Officer_Name2', 'FitnessOfficerName2', 'fitness_officer_name2', 'fitnessOfficerName2']),
      Office: getValue(f, ['Office', 'office']),
      Remark: getValue(f, ['Remark', 'remark']) || '',
    }));
  };

  // Map Changed Vehicle data - exact column names from history_of_changed_vehicle_record_by_admins table (n8n may send Serial_No)
  const mapChangedVehicle = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((c) => ({
      Sr_No: getValue(c, ['Sr_No', 'SrNo', 'sr_no', 'srno', 'srNo', 'Serial_No', 'Serial_no', 'serialNo']),
      Changed_By: getValue(c, ['Changed_By', 'ChangedBy', 'changed_by', 'changedBy']),
      Changed_Data: getValue(c, ['Changed_Data', 'ChangedData', 'changed_data', 'changedData']),
      Changed_On: getValue(c, ['Changed_On', 'ChangedOn', 'changed_on', 'changedOn']),
      Office: getValue(c, ['Office', 'office']),
    }));
  };

  // Map RC Print data - exact column names from history_of_rc_print_details table
  const mapRcPrint = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((r) => ({
      Application_No: getValue(r, ['Application_No', 'ApplicationNo', 'application_no', 'applicationNo']),
      Purpose: getValue(r, ['Purpose', 'purpose']),
      Printed_On: getValue(r, ['Printed_On', 'PrintedOn', 'printed_on', 'printedOn']),
      Printed_By: getValue(r, ['Printed_By', 'Printed_by', 'PrintedBy', 'printed_by', 'printedBy']),
    }));
  };

  // Return normalized data with exact table names as keys; fill Sr_No/Serial_No as 1,2,3... when missing
  return {
    historyOfChallan: ensureSerialNumbers(
      mapChallan(asArray(
        data.history_of_challans ||
        data.historyOfChallan ||
        data.History_of_Challan ||
        data.HistoryOfChallan
      )),
      'Serial_No',
      'Sr_No'
    ),
    historyOfHypothecation: mapHypothecation(asArray(
      data.history_of_hypothecations || 
      data.historyOfHypothecation || 
      data.History_of_Hypothecation || 
      data.HistoryOfHypothecation
    )),
    historyOfInsurance: ensureSerialNumbers(
      mapInsurance(asArray(
        data.history_of_insurances ||
        data.historyOfInsurance ||
        data.History_of_Insurance ||
        data.HistoryOfInsurance
      ))
    ),
    historyOfPaidFees: mapPaidFees(asArray(
      data.history_of_paid_fees || 
      data.historyOfPaidFees || 
      data.History_of_Paid_Fees || 
      data.HistoryOfPaidFees
    )),
    historyOfRoadTax: mapRoadTax(asArray(
      data.history_of_road_taxes || 
      data.historyOfRoadTax || 
      data.History_of_Road_Tax || 
      data.HistoryOfRoadTax
    )),
    historyOfOwnership: mapOwnership(asArray(
      data.history_of_transfer_ownerships || 
      data.historyOfOwnership || 
      data.History_of_Ownership_Transfer || 
      data.HistoryOfOwnershipTransfer
    )),
    historyOfBlacklist: ensureSerialNumbers(
      mapBlacklist(asArray(
        data.history_of_blacklists ||
        data.historyOfBlacklist ||
        data.History_of_Blacklist ||
        data.HistoryOfBlacklist
      ))
    ),
    historyOfPermit: ensureSerialNumbers(
      mapPermit(asArray(
        data.history_of_permits ||
        data.historyOfPermit ||
        data.History_of_Permit ||
        data.HistoryOfPermit
      ))
    ),
    historyOfTaxClear: ensureSerialNumbers(
      mapTaxClear(asArray(
        data.history_of_tax_clears ||
        data.historyOfTaxClear ||
        data.History_of_Tax_Clear ||
        data.HistoryOfTaxClear
      ))
    ),
    historyOfFitness: mapFitness(asArray(
      data.history_of_fitness || 
      data.historyOfFitness || 
      data.History_of_Fitness || 
      data.HistoryOfFitness
    )),
    historyOfChangedVehicle: ensureSerialNumbers(
      mapChangedVehicle(asArray(
        data.history_of_changed_vehicle_record_by_admins ||
        data.historyOfChangedVehicle ||
        data.History_of_Changed_Vehicle_Record_by_Admin ||
        data.HistoryOfChangedVehicleRecordByAdmin
      ))
    ),
    historyOfRcPrint: mapRcPrint(asArray(
      data.history_of_rc_print_details || 
      data.historyOfRcPrint || 
      data.History_of_RC_Print_Details || 
      data.HistoryOfRCPrintDetails
    )),
  };
};
